/**
 * A connection string → the New connection form's values (owner, 30.09.2026:
 * paste `mysql://user:pass@host:3306/db` from an .env instead of filling every
 * field). Injected into the DB Manager guest with Function.prototype.toString,
 * so it stays self-contained ES5 (see treeRefresh.js). Jest imports it.
 *
 * Returns { ok: true, engine, host, port, database, user, password, sslMode,
 * sslRootCert, sslClientCert, sslClientKey, path, ignored: [names] } or
 * { ok: false, error }. The caller opens the normal form PREFILLED, so the user
 * reviews everything before Connect; the password goes on through the usual
 * vault / runtime path. Nothing here stores or logs the string.
 */
export function parseConnectionUrl(input) {
  var fail = function (error) { return { ok: false, error: error }; };
  var text = String(input == null ? '' : input).trim();
  // `DATABASE_URL=…` straight from an .env, quotes included.
  text = text.replace(/^(export\s+)?[A-Z][A-Z0-9_]*\s*=\s*/, '');
  if ((text.charAt(0) === '"' && text.charAt(text.length - 1) === '"') || (text.charAt(0) === "'" && text.charAt(text.length - 1) === "'")) {
    text = text.slice(1, -1).trim();
  }
  if (!text) return fail('Paste a connection URL, e.g. postgres://user:password@host:5432/database');

  var schemeMatch = text.match(/^([a-z][a-z0-9+.-]*):/i);
  var scheme = schemeMatch ? schemeMatch[1].toLowerCase() : '';

  // SQLite: sqlite:///path, sqlite:path, file:path, or a bare path to a db file.
  if (scheme === 'sqlite' || scheme === 'sqlite3' || scheme === 'file' || (!scheme && /\.(sqlite3?|db3?)$/i.test(text)) || (/^[a-z]:[\\/]/i.test(text) && /\.(sqlite3?|db3?)$/i.test(text))) {
    var path = text;
    if (scheme === 'sqlite' || scheme === 'sqlite3' || scheme === 'file') {
      path = text.slice(schemeMatch[0].length).replace(/^\/\/(localhost)?/, '');
      // sqlite:///C:/data/a.db → C:/data/a.db (a Windows drive after the slash)
      if (/^\/[a-z]:[\\/]/i.test(path)) path = path.slice(1);
      try { path = decodeURIComponent(path); } catch (e) { /* keep as typed */ }
    }
    if (!path) return fail('The SQLite URL has no file path.');
    return { ok: true, engine: 'sqlite', path: path, ignored: [] };
  }

  var engine = { mysql: 'mysql', mariadb: 'mysql', postgres: 'postgres', postgresql: 'postgres' }[scheme];
  if (!engine) return fail('Not a supported connection URL — use mysql://, mariadb://, postgres://, postgresql:// or sqlite://.');

  var rest = text.slice(schemeMatch[0].length);
  if (rest.indexOf('//') !== 0) return fail('The URL needs // after the scheme, e.g. ' + scheme + '://host');
  var authority = rest.slice(2).split(/[/?#]/)[0];
  var hostPart = authority.slice(authority.lastIndexOf('@') + 1);
  if (hostPart.indexOf(',') !== -1) return fail('Several hosts in one URL are not supported — use one host.');
  if (/^%2f/i.test(hostPart) || hostPart.charAt(0) === '/') return fail('Unix-socket URLs are not supported — use a host name.');

  var url;
  try {
    url = new URL('http:' + rest);
  } catch (e) {
    return fail('That does not look like a valid URL.');
  }
  var decode = function (v) {
    try { return decodeURIComponent(v || ''); } catch (e) { return v || ''; }
  };
  var host = url.hostname;
  if (!host) return fail('The URL has no host.');
  if (host.charAt(0) === '[' && host.charAt(host.length - 1) === ']') host = host.slice(1, -1);
  var defaults = { mysql: { port: '3306', user: 'root' }, postgres: { port: '5432', user: 'postgres' } }[engine];
  var params = url.searchParams;
  var sslMode = '';
  var ignored = [];
  var pgModes = { disable: 'disable', allow: 'prefer', prefer: 'prefer', require: 'require', 'verify-ca': 'verify_ca', 'verify-full': 'verify_full' };
  var myModes = { disabled: 'disable', preferred: 'prefer', required: 'require', verify_ca: 'verify_ca', verify_identity: 'verify_full' };
  var out = {
    ok: true,
    engine: engine,
    host: host,
    port: url.port || defaults.port,
    database: decode(url.pathname.replace(/^\//, '')),
    user: decode(url.username) || defaults.user,
    password: decode(url.password),
    sslMode: '',
    sslRootCert: '',
    sslClientCert: '',
    sslClientKey: '',
    path: '',
    ignored: ignored,
  };
  params.forEach(function (value, key) {
    var k = key.toLowerCase().replace(/-/g, '_');
    var v = String(value).toLowerCase();
    if (k === 'sslmode' || k === 'ssl_mode') {
      sslMode = engine === 'postgres' ? pgModes[v] || '' : myModes[v.replace(/-/g, '_')] || pgModes[v] || '';
      if (!sslMode) ignored.push(key);
    } else if (k === 'sslrootcert' || k === 'ssl_ca') out.sslRootCert = value;
    else if (k === 'sslcert' || k === 'ssl_cert') out.sslClientCert = value;
    else if (k === 'sslkey' || k === 'ssl_key') out.sslClientKey = value;
    else ignored.push(key);
  });
  out.sslMode = sslMode;
  return out;
}
